import { useState } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { z } from 'zod'
import { ArrowLeft, ArrowRight, Building2, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AuthShell } from '@/components/Auth/AuthShell'

const step1Schema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  full_name: z.string().min(2, 'Full name is required'),
})

type AccountType = 'individual' | 'institution'

export default function PortalRegister() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [step, setStep] = useState(1)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [step1, setStep1] = useState({ email: '', password: '', full_name: '' })
  const [accountType, setAccountType] = useState<AccountType | null>(null)
  const [step3, setStep3] = useState({ bio: '' })

  const handleStep1 = () => {
    const result = step1Schema.safeParse(step1)
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      result.error.errors.forEach(err => { if (err.path[0]) fieldErrors[err.path[0] as string] = err.message })
      setErrors(fieldErrors); return
    }
    setErrors({}); setStep(2)
  }

  const handleStep2 = (type: AccountType) => { setAccountType(type); setStep(3) }

  const handleSubmit = async () => {
    if (!accountType) return
    setLoading(true)
    try {
      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: step1.email,
        password: step1.password,
        options: { data: { full_name: step1.full_name } },
      })
      if (signUpError) throw signUpError
      if (!authData.user) throw new Error('Account creation failed')

      const { error: profileError } = await (supabase.from('journalist_profiles').insert({
        id: authData.user.id,
        email: step1.email,
        full_name: step1.full_name,
        account_type: accountType,
        bio: step3.bio || null,
      }) as any)

      if (profileError) {
        // The auth account exists but has no profile — PortalRouteGuard would bounce
        // this user forever, so surface the failure instead of a false "success".
        throw new Error(`Your account was created but the profile setup failed: ${profileError.message}. Please contact support before signing in.`)
      }

      toast({ title: 'Account created! Please check your email to verify.' })
      navigate('/portal/login')
    } catch (err: any) {
      toast({ title: err.message || 'Registration failed', variant: 'destructive' })
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell
      logoLabel="Journalists"
      title="Create your journalist account"
      subtitle={`Step ${step} of 3, set up your portal access.`}
      quote="One identity, every accreditation you need."
      quoteAuthor="CONORBIT Portal"
      backHref="/portal/login"
      backLabel="Back to sign in"
    >
      <div className="flex items-center justify-center gap-2 mb-8">
        {[1, 2, 3].map(s => (
          <div key={s} className={cn(
            'h-1.5 rounded-full transition-all',
            step === s ? 'w-10 bg-accent' : step > s ? 'w-6 bg-primary-glow' : 'w-6 bg-muted'
          )} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="full_name">Full Name</Label>
            <Input id="full_name" value={step1.full_name} className="h-11"
              onChange={e => setStep1(f => ({ ...f, full_name: e.target.value }))} placeholder="Jane Doe" />
            {errors.full_name && <p className="text-justify text-destructive text-xs">{errors.full_name}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">Email</Label>
            <Input id="email" type="email" value={step1.email} className="h-11"
              onChange={e => setStep1(f => ({ ...f, email: e.target.value }))} placeholder="your@email.com" />
            {errors.email && <p className="text-justify text-destructive text-xs">{errors.email}</p>}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={step1.password} className="h-11"
              onChange={e => setStep1(f => ({ ...f, password: e.target.value }))} placeholder="Min. 8 characters" />
            {errors.password && <p className="text-justify text-destructive text-xs">{errors.password}</p>}
          </div>
          <Button onClick={handleStep1} className="w-full h-11 shadow-glow">
            Continue <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <p className="text-justify text-foreground font-medium text-center mb-2">What best describes you?</p>
          <button onClick={() => handleStep2('individual')}
            className="w-full text-left p-5 border-2 border-border rounded-xl hover:border-accent transition-colors group bg-card">
            <div className="flex items-start gap-3">
              <User className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-foreground">Individual Journalist</div>
                <p className="text-justify text-sm text-muted-foreground mt-1 leading-relaxed">
                  I work independently or freelance and represent only myself.
                </p>
              </div>
            </div>
          </button>
          <button onClick={() => handleStep2('institution')}
            className="w-full text-left p-5 border-2 border-border rounded-xl hover:border-accent transition-colors group bg-card">
            <div className="flex items-start gap-3">
              <Building2 className="w-5 h-5 text-accent mt-0.5 shrink-0" />
              <div>
                <div className="font-semibold text-foreground">Media Institution</div>
                <p className="text-justify text-sm text-muted-foreground mt-1 leading-relaxed">
                  I represent a media house and will manage a team of journalists.
                </p>
              </div>
            </div>
          </button>
          <button onClick={() => setStep(1)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="bio">Bio <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea id="bio" value={step3.bio}
              onChange={e => setStep3(s => ({ ...s, bio: e.target.value }))}
              placeholder="A brief description of your journalistic focus or expertise…" rows={3} />
          </div>
          <p className="text-justify text-xs text-muted-foreground -mt-1">
            Your contact details will be visible to Communications Officers at organisations you're accredited to — this is a requirement of accreditation.
          </p>
          <Button onClick={handleSubmit} disabled={loading} className="w-full h-11 shadow-glow">
            {loading ? 'Creating account…' : 'Create Account'}
          </Button>
          <button onClick={() => setStep(2)}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mx-auto">
            <ArrowLeft className="w-3.5 h-3.5" /> Back
          </button>
        </div>
      )}

      <div className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/portal/login" className="text-accent hover:underline font-medium">Sign in</Link>
      </div>
    </AuthShell>
  )
}
